/**
 * Drag & Drop Activity
 * Students drag items from a bank into categorized drop zones
 */

import { useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { CheckCircle, XCircle, GripVertical, ArrowDownToLine, Package } from 'lucide-react';
import { motion } from 'motion/react';
import type { ActivitySlideProps } from '../types';
import { getDndBackend } from './dndBackend';
import { stripHtml } from '../../../../utils/slides/stripHtml';

const ZONE_COLORS = [
  { bg: 'bg-blue-500/10', border: 'border-blue-500/40', accent: 'bg-blue-500', text: 'text-blue-600' },
  { bg: 'bg-orange-500/10', border: 'border-orange-500/40', accent: 'bg-orange-500', text: 'text-orange-600' },
  { bg: 'bg-emerald-500/10', border: 'border-emerald-500/40', accent: 'bg-emerald-500', text: 'text-emerald-600' },
  { bg: 'bg-purple-500/10', border: 'border-purple-500/40', accent: 'bg-purple-500', text: 'text-purple-600' },
];

interface DragItem {
  id: string;
  label: string;
  targetId: string; // correct target ID
}

interface DropTarget {
  id: string;
  label: string;
}

const DRAG_TYPE = 'DRAG_DROP_ITEM';

/**
 * Strip embedded " → Category" or " -> Category" from legacy item labels
 * that may have stored the full "Dog → Animals" as the label text.
 */
function cleanItemLabel(raw: string): string {
  let sep = raw.lastIndexOf(' → ');
  if (sep < 0) sep = raw.lastIndexOf(' -> ');
  return sep >= 0 ? raw.slice(0, sep).trim() : raw;
}

function DraggableChip({
  item,
  hasSubmitted,
  isCorrect,
  selected,
  actionLabel,
  onTap,
  fullWidth,
}: {
  item: DragItem;
  hasSubmitted: boolean;
  isCorrect?: boolean;
  selected?: boolean;
  actionLabel: string;
  onTap: () => void;
  fullWidth?: boolean;
}) {
  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: { id: item.id },
    canDrag: !hasSubmitted,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const baseClass =
    'flex min-h-11 items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all select-none';
  const stateClass = hasSubmitted
    ? isCorrect
      ? 'bg-accent/10 border-accent/40 text-accent'
      : 'bg-destructive/10 border-destructive/40 text-destructive'
    : 'bg-card border-border/50 hover:border-primary/60 hover:shadow-md cursor-move';

  return (
    <motion.button
      type="button"
      ref={drag as any}
      onClick={onTap}
      disabled={hasSubmitted}
      aria-pressed={selected}
      aria-label={`${actionLabel}: ${cleanItemLabel(item.label)}`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={!hasSubmitted ? { scale: 1.03 } : undefined}
      whileTap={!hasSubmitted ? { scale: 0.95 } : undefined}
      className={`${baseClass} ${fullWidth ? 'w-full' : ''} text-left ${stateClass} ${selected ? 'ring-2 ring-primary border-primary bg-primary/10' : ''} ${isDragging ? 'opacity-30 scale-90 shadow-xl' : ''} touch-none`}
    >
      {!hasSubmitted && (
        <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      )}
      <span className="flex-1">{cleanItemLabel(item.label)}</span>
      {hasSubmitted && isCorrect !== undefined &&
        (isCorrect
          ? <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
          : <XCircle className="w-4 h-4 text-destructive flex-shrink-0" />)}
    </motion.button>
  );
}

function ItemsBank({
  items,
  hasSubmitted,
  onReturn,
  selectedItemId,
  onSelect,
}: {
  items: DragItem[];
  hasSubmitted: boolean;
  onReturn: (itemId: string) => void;
  selectedItemId: string | null;
  onSelect: (itemId: string) => void;
}) {
  const [{ isOver }, drop] = useDrop({
    accept: DRAG_TYPE,
    drop: (dragged: { id: string }) => onReturn(dragged.id),
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  return (
    <div
      ref={drop as any}
      className={`flex flex-wrap gap-2 min-h-[64px] p-3 rounded-2xl border-2 border-dashed transition-all touch-none ${
        isOver ? 'border-primary bg-primary/5 shadow-inner' : 'border-border/50 bg-muted/10'
      }`}
    >
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground self-center w-full text-center">
          All items placed — drag back here to reposition
        </p>
      ) : (
        items.map((item) => (
          <DraggableChip
            key={item.id}
            item={item}
            hasSubmitted={hasSubmitted}
            actionLabel="Select item"
            selected={selectedItemId === item.id}
            onTap={() => onSelect(item.id)}
          />
        ))
      )}
    </div>
  );
}

function DropZone({
  target,
  placedItems,
  hasSubmitted,
  onDrop,
  onReturn,
  selectedItem,
  onTapPlace,
  colorIndex = 0,
}: {
  target: DropTarget;
  placedItems: DragItem[];
  hasSubmitted: boolean;
  onDrop: (itemId: string, targetId: string) => void;
  onReturn: (itemId: string) => void;
  selectedItem: DragItem | null;
  onTapPlace: (itemId: string, targetId: string) => void;
  colorIndex?: number;
}) {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: DRAG_TYPE,
    drop: (dragged: { id: string }) => onDrop(dragged.id, target.id),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const color = ZONE_COLORS[colorIndex % ZONE_COLORS.length];

  return (
    <div
      ref={drop as any}
      className={`min-h-[110px] rounded-2xl border-2 p-3 transition-all touch-none ${
        isOver && canDrop
          ? `${color.border} ${color.bg} shadow-lg`
          : `border-dashed border-border/50 bg-muted/10`
      }`}
    >
      <div className="flex items-center gap-2 justify-center mb-3 pb-2 border-b border-border/30">
        <div className={`w-3 h-3 rounded-full ${color.accent}`}></div>
        <p className={`text-sm font-bold ${color.text}`}>
          {target.label}
        </p>
      </div>
      <div className="space-y-2">
        {placedItems.map((item) => (
          <DraggableChip
            key={item.id}
            item={item}
            hasSubmitted={hasSubmitted}
            isCorrect={hasSubmitted ? item.targetId === target.id : undefined}
            actionLabel="Return item to the bank"
            onTap={() => onReturn(item.id)}
            fullWidth
          />
        ))}
      </div>
      {placedItems.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-2">
          Drop items here
        </p>
      )}
      {selectedItem && !hasSubmitted && (
        <button
          type="button"
          onClick={() => onTapPlace(selectedItem.id, target.id)}
          className="mt-3 min-h-11 w-full rounded-xl border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-semibold text-primary"
        >
          Place “{cleanItemLabel(selectedItem.label)}” here
        </button>
      )}
    </div>
  );
}

export default function DragDrop({
  slide,
  selectedAnswer,
  onAnswerChange,
  hasSubmitted,
  className = '',
}: ActivitySlideProps) {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const { content } = slide;
  // Sanitise items: clean legacy labels that may include " → Category"
  const items: DragItem[] = (content?.items || []).map((item: DragItem) => ({
    ...item,
    label: cleanItemLabel(item.label),
  }));
  const targets: DropTarget[] = content?.targets || [];
  const placements: Record<string, string> = selectedAnswer || {};
  const { backend, options } = getDndBackend();

  const handleDrop = (itemId: string, targetId: string) => {
    onAnswerChange({ ...placements, [itemId]: targetId });
    setSelectedItemId(null);
  };

  const handleReturn = (itemId: string) => {
    const updated = { ...placements };
    delete updated[itemId];
    onAnswerChange(updated);
    setSelectedItemId((selected) => selected === itemId ? null : selected);
  };

  const selectedItem = items.find((item) => item.id === selectedItemId && !placements[item.id]) || null;
  const unplaced = items.filter((item) => !placements[item.id]);
  const inTarget = (targetId: string) =>
    items.filter((item) => placements[item.id] === targetId);

  const cols =
    targets.length === 1
      ? 'grid-cols-1'
      : targets.length === 2
      ? 'grid-cols-1 sm:grid-cols-2'
      : targets.length === 3
      ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'
      : 'grid-cols-1 sm:grid-cols-2';

  return (
    <DndProvider backend={backend} options={options}>
      <div className={`w-full ${className}`}>
        <div className="bg-gradient-to-br from-emerald-500/5 via-blue-500/5 to-purple-500/5 rounded-3xl p-5 sm:p-6 border border-border/40">
          {/* Header */}
          <div className="text-center mb-5">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-600 px-4 py-1.5 rounded-full mb-2">
              <Package className="w-4 h-4" />
              <span className="text-sm font-bold">Drag & Drop</span>
            </div>
            {content?.instruction && (
              <div className="text-sm text-muted-foreground">
                {stripHtml(content.instruction)}
              </div>
            )}
            <div className="mt-1 text-xs text-muted-foreground" aria-live="polite">
              {selectedItem
                ? `Selected: ${cleanItemLabel(selectedItem.label)}. Tap a category button to place it.`
                : 'Tap an item, then tap a category button, or drag items into a category.'}
            </div>
            {/* Progress */}
            <div className="flex items-center justify-center gap-2 mt-2">
              <span className="text-xs text-muted-foreground font-medium">
                {Object.keys(placements).length} of {items.length} sorted
              </span>
              <div className="flex gap-1">
                {items.map((item, i) => (
                  <div key={item.id} className={`w-2 h-2 rounded-full transition-all ${
                    placements[item.id] ? 'bg-emerald-500' : 'bg-muted-foreground/20'
                  }`} />
                ))}
              </div>
            </div>
          </div>

          {/* Items bank */}
          <div className="mb-5">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
              <ArrowDownToLine className="w-3.5 h-3.5" />
              Items to Sort
            </p>
            <ItemsBank
              items={unplaced}
              hasSubmitted={hasSubmitted}
              onReturn={handleReturn}
              selectedItemId={selectedItemId}
              onSelect={(itemId) => setSelectedItemId((selected) => selected === itemId ? null : itemId)}
            />
          </div>

          {/* Drop zones */}
          {targets.length > 0 && (
            <div className={`grid gap-4 ${cols}`}>
              {targets.map((target, idx) => (
                <DropZone
                  key={target.id}
                  target={target}
                  placedItems={inTarget(target.id)}
                  hasSubmitted={hasSubmitted}
                  onDrop={handleDrop}
                  onReturn={handleReturn}
                  selectedItem={selectedItem}
                  onTapPlace={handleDrop}
                  colorIndex={idx}
                />
              ))}
            </div>
          )}

          {targets.length === 0 && (
            <p className="text-center text-muted-foreground text-sm py-6">
              No drop zones configured yet.
            </p>
          )}
        </div>
      </div>
    </DndProvider>
  );
}
